import { Button, Popover, Tooltip } from "antd";
import InlineEditTitle from "components/inline-edit-title";
import { CiqIconBox, CopyIcon } from "components/svg-icons";
import { useEffect, useState } from "react";
import ErrorBoundary from "components/error-boundary";
import { ArrowLeftOutlined } from "@ant-design/icons";
import FeatureDuplicate from "components/feature-duplicate";
import { FeatureTypes } from "entity-app/constants";
import FeatureLogNavigator from "components/feature-log-navigator";
import type { FeatureLogNavReaderResult } from "hooks/use-feature-log-nav-reader";

function FeatureHeader(props: {
  featureInstanceData?: any;
  title: string;
  subTitle?: string;
  actions?: any;
  headerTitle?: string;
  number?: string;
  status?: any;
  disabled?: boolean;
  feature: "MATERIALS";
  goBackToList: () => void;
  navigator?: FeatureLogNavReaderResult;
}) {
  const {
    featureInstanceData,
    actions,
    title,
    subTitle,
    headerTitle,
    number,
    status,
    disabled,
    feature,
    goBackToList,
    navigator
  } = props;

  const [visible, setVisible] = useState(false);
  const [headerInfo, setHeaderInfo] = useState<{
    featureHeaderNumber?: string;
    featureHeaderTitle?: string;
  }>();

  const updateHeaderInfo = async (headersInfo: any) => {
    setHeaderInfo({
      featureHeaderNumber: headersInfo?.number,
      featureHeaderTitle: headersInfo?.title
    });
    if (headersInfo?.title === headerInfo?.featureHeaderTitle) {
      return;
    }

    if (actions?.bulkEditFeatureInstanceFields) {
      await actions.bulkEditFeatureInstanceFields({
        title: headersInfo?.title
      });
    }
  };

  useEffect(() => {
    setHeaderInfo({
      featureHeaderNumber: number,
      featureHeaderTitle: headerTitle
    });
  }, [headerTitle, number]);

  return (
    <div
      className="flex flex-col w-full bg-white px-6 py-4 space-y-2"
      data-testid="feature-header-root"
    >
      <div
        className="flex justify-between items-center"
        data-testid="feature-header-top-row"
      >
        <div
          className="flex space-x-2 items-center"
          data-testid="feature-header-title-row"
        >
          <Button
            icon={<ArrowLeftOutlined />}
            className="text-one text-black !bg-transparent !border-0 px-0 !shadow-none"
            onClick={goBackToList}
            data-testid="feature-header-back-btn"
          />
          <CiqIconBox
            feature={feature}
            size={20}
            padding={8}
            data-testid="feature-header-icon"
          />
          <div className="flex-col pl-1" data-testid="feature-header-title-col">
            <div
              className="flex items-center space-x-2"
              data-testid="feature-header-title-row-inner"
            >
              <div
                className="text-xl font-semibold"
                data-testid="feature-header-title"
              >
                {title}
              </div>
            </div>
            {subTitle && (
              <div
                className="text-xs font-normal"
                data-testid="feature-header-subtitle"
              >
                {subTitle}
              </div>
            )}
          </div>
          {navigator?.visible && (
            <FeatureLogNavigator
              visible={navigator.visible}
              currentIndex={navigator.currentIndex}
              total={navigator.total}
              onPrev={navigator.goPrev}
              onNext={navigator.goNext}
            />
          )}
        </div>
        <div
          className="flex space-x-2 items-center"
          data-testid="feature-header-actions"
        >
          <Popover
            open={visible}
            placement="bottomRight"
            content={
              <ErrorBoundary>
                <FeatureDuplicate
                  featureInstanceData={featureInstanceData}
                  actions={actions}
                  currentFeatureType={FeatureTypes.MATERIALS}
                  linkedSourcefeatureType={FeatureTypes.SUBMITTALS}
                  setVisible={setVisible}
                  visible={visible}
                  data-testid="feature-header-duplicate"
                />
              </ErrorBoundary>
            }
            trigger="click"
            onOpenChange={() => {
              // The only trigger for this popover is the Duplicate button,
              // which carries the same `disabled` flag. React never dispatches
              // mouse events on a disabled <button>, so onOpenChange cannot
              // fire while `disabled` is true - the else arm is unreachable.
              /* v8 ignore next */
              if (!disabled) setVisible(!visible);
            }}
            destroyOnHidden
            data-testid="feature-header-duplicate-popover"
          >
            <Tooltip data-testid="feature-header-duplicate-tooltip">
              <Button
                onClick={() => setVisible(true)}
                disabled={disabled}
                data-testid="feature-header-duplicate-btn"
              >
                <div
                  className="flex items-center space-x-2"
                  data-testid="feature-header-duplicate-btn-content"
                >
                  <div className=" text-[12px]">Duplicate</div>
                  <CopyIcon data-testid="feature-header-duplicate-icon" />
                </div>
              </Button>
            </Tooltip>
          </Popover>
        </div>
      </div>
      <div
        className="flex py-2 md:px-16 lg:px-24 sm:px-4 items-center justify-center"
        data-testid="feature-header-edit-row"
      >
        <div
          className="flex w-full p-4 bg-ciq-gray-50 bg-opacity-60 items-center justify-between"
          data-testid="feature-header-edit-bg"
        >
          <ErrorBoundary>
            <InlineEditTitle
              isPermissionToEdit={!disabled}
              headerInfo={headerInfo}
              updateHeaderInfo={updateHeaderInfo}
              feature={feature}
              data-testid="feature-header-inline-edit-title"
            />
          </ErrorBoundary>
          <div
            className="flex items-center"
            data-testid="feature-header-status"
          >
            {status}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeatureHeader;
